import React, {useEffect, useRef, forwardRef, Ref, useImperativeHandle, useState} from "react";

import MessageConst from "../../const/Message.const";
import visaCard from "../../static/images/card_visa.png";
import amexCard from "../../static/images/card_amex.png";
import mastercardCard from "../../static/images/card_mastercard.png";
import unionpayCard from "../../static/images/card_unionpay.png";
import discoverCard from "../../static/images/card_discover.png";
import CSVHelp from "../../static/images/CSV-help.png";
import crossSvg from "../../static/images/cross.svg";
import { logToBackend } from "../../utils/logger";

import "./styles.scss";
import PopUp from "../PopUp";

const MONERIS_ERROR_CODES: Record<string, string> = {
	// Card validation errors
	"940": "Invalid profile id",
	"941": "Error generating token",
	"942": "Invalid transaction",
	"943": "Invalid card number",
	"944": "Invalid expiry date",
	"945": "Invalid CVV",
	// Additional known error codes
	"001": "Transaction not approved",
	"050": "Transaction declined",
	"051": "Expired card",
	"052": "PIN retries exceeded",
	"053": "No sharing",
	"054": "No security module",
	"055": "Invalid transaction",
	"056": "No support",
	"057": "Lost or stolen card",
	"058": "Invalid status",
	"059": "Restricted card",
	"060": "No chequing account",
	"061": "No savings account",
	"062": "No account type",
	"063": "Account not supported",
	"064": "Already reversed",
};

const CARD_INPUT_ERROR_CODES = ["943", "944", "945"];
const TOKENIZATION_ERROR_CODES = ["940", "941", "942"];
const DECLINED_CODES = ["050", "051", "057", "059"];

const normalizeResponseCodes = (responseCode: unknown): string[] => {
	if (!responseCode) return [];
	if (Array.isArray(responseCode)) return responseCode.map(String);
	return [String(responseCode)];
};

const parseMonerisObject = (data: string): Record<string, unknown> | null => {
	try {
		const parsed = JSON.parse(data);
		return typeof parsed === "object" && parsed !== null ? parsed : null;
	} catch {
		return null;
	}
};

export interface MonerisInputRefObject {
	doMonerisSubmit: () => void;
}

interface MonerisInputProps {
	addCb: (token: string) => void;
	setValidationMessage: (msg: string) => void;
	setCheckingCard: (b: boolean) => void;
	shrink: boolean;
}

const MonerisInput = forwardRef(
	(
		{addCb, setValidationMessage, setCheckingCard, shrink}: MonerisInputProps,
		ref: Ref<MonerisInputRefObject>
	) => {
		const [showCSVHelp, setShowCSVHelp] = useState<boolean>(false);

		useImperativeHandle(ref, () => ({doMonerisSubmit}));

		const monFrameRef = useRef<HTMLIFrameElement>(null);

		const doMonerisSubmit = () => {
			const refObj = monFrameRef.current!.contentWindow;
			refObj!.postMessage("tokenize", process.env.REACT_APP_MONERIS_URL!);
			// change link according to table above
		};

		useEffect(() => {
			const respMsg = (e: any) => {
				if (!e?.data || typeof e.data !== "string") return;

				// Ignore non-object messages (e.g. Moneris iframe lifecycle codes like 979, 991)
				const result = parseMonerisObject(e.data);
				if (!result) {
					logToBackend("Ignoring non-object Moneris message", { raw: e.data });
					return;
				}

				try {
					console.log(result);
					logToBackend("Moneris response received", result);

					const codes = normalizeResponseCodes(result?.responseCode);

					// 1. Card input errors (invalid number, expiry, cvv)
					if (codes.some((code) => CARD_INPUT_ERROR_CODES.includes(code))) {
						const errorCode = codes.find((c) => CARD_INPUT_ERROR_CODES.includes(c));
						const detail = errorCode ? MONERIS_ERROR_CODES[errorCode] : "";
						logToBackend("Card input validation failed", { codes, detail });
						setValidationMessage(MessageConst.validation.generalCardError);

					// 2. Tokenization / system errors
					} else if (codes.some((code) => TOKENIZATION_ERROR_CODES.includes(code))) {
						const errorCode = codes.find((c) => TOKENIZATION_ERROR_CODES.includes(c));
						const detail = errorCode ? MONERIS_ERROR_CODES[errorCode] : "";
						logToBackend("Tokenization error", { codes, detail });
						setValidationMessage(MessageConst.error.unknownError);

					// 3. Card declined
					} else if (codes.some((code) => DECLINED_CODES.includes(code))) {
						logToBackend("Card declined", { codes });
						setValidationMessage(MessageConst.validation.generalCardError);

					// 4. Any other non-empty error code we don't explicitly handle
					} else if (codes.length > 0 && codes.some((code) => code !== "001" && MONERIS_ERROR_CODES[code])) {
						logToBackend("Unhandled Moneris error code", { codes });
						setValidationMessage(MessageConst.error.unknownError);

					// 5. Success — but only if dataKey is actually present
					} else if (result?.dataKey && typeof result.dataKey === "string" && result.dataKey.trim() !== "") {
						logToBackend("Token received successfully", { dataKey: result.dataKey });
						addCb(result.dataKey);
						setValidationMessage("");

					// 6. No error code, no dataKey — session timeout, incomplete fields, env mismatch
					} else {
						logToBackend("Moneris response missing dataKey and no recognized error", {
							codes,
							hasDataKey: !!result?.dataKey,
							fullResponse: result,
						});
						setValidationMessage(
							"Unable to process your card. Please refresh the page and try again."
						);
					}
				} catch (err) {
					logToBackend("Failed to parse Moneris response", { raw: e.data, err });
					setValidationMessage(MessageConst.error.unknownError);
				} finally {
					setCheckingCard(false);
				}
			};

			window.addEventListener("message", respMsg, false);

			return () => {
				window.removeEventListener("message", respMsg);
			};
		}, [addCb, setValidationMessage, setCheckingCard]);

		return (
			<div className="card-number">
				<div className="card-number__cardIcons">
					<img className="card-number__cardImg" src={visaCard} alt="visa" />
					<img className="card-number__cardImg" src={mastercardCard} alt="mastercard" />
					<img className="card-number__cardImg" src={amexCard} alt="amex" />
					<img className="card-number__cardImg" src={discoverCard} alt="discover" />
					<img className="card-number__cardImg" src={unionpayCard} alt="unionpay" />
				</div>
				<div className="titles">
					<div className={`card-number-title ${shrink ? "shrinked" : ""}`}>Card Number</div>
					<div className={`expiry-date-title ${shrink ? "shrinked" : ""}`}>
						{shrink ? "Exp.Date" : "Expiration Date"}
					</div>
					<div className="cvd-title">
						{shrink ? "CVC" : "Security Code"}{" "}
					</div>
				</div>
				<div className="card-number-wrapper">
					<div id="monerisResponse" />
					<iframe
						id="monerisFrame"
						ref={monFrameRef}
						title="Moneris"
						src={`${process.env.REACT_APP_MONERIS_URL}?id=${
							process.env.REACT_APP_MONERIS_PROFILE
						}&pmmsg=true&
						css_body=background:white;width:100%;display: flex;
						align-items: center; padding-left: 3px; box-sizing: border-box;
						&css_textbox=height: 40px; margin-right: 10px; border:1px solid rgb(187,145,87);
						outline-color: black; box-sizing: border-box; font-size: 14px; 
						padding: 8px 10px; border-radius: 8px;
						&css_textbox_pan=width:${shrink ? "150px" : "197px"};
						font-size: 14px;&enable_exp=1&display_labels=2&
						css_textbox_exp=width:70px;margin-right: ${shrink ? "10px" : "50px"};
						&enable_cvd=1&
						css_textbox_cvd=width:52px&exp_label=MMYY&cvd_label=CVC`}
						frameBorder="0"
						width="100%"
						height="44px"
						style={{position: "relative", left: "-3px"}}
					/>
				</div>
				{showCSVHelp && (
					<PopUp>
						<div className="show-csv-help-wrapper">
							<div className="close" onClick={() => setShowCSVHelp(false)}>
								<img src={crossSvg} alt="" />
							</div>
							<div className="title">
								<span>
									Your security code (CVV or CVC) is 3 or 4 digit code found on your credit card in
									the following locations:
								</span>
							</div>
							<img src={CSVHelp} alt="" className="help-image" />
						</div>
					</PopUp>
				)}
			</div>
		);
	}
);

export default MonerisInput;
